import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { modulePolicyFor } from '@/lib/sessionPolicy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Default policy: 20 minutes. A shortened value may be set for testing via
// localStorage 'te_timeout_ms' (client) and env INACTIVITY_TIMEOUT_SECONDS (server).
const DEFAULT_TIMEOUT_MS = 20 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'pointerdown', 'wheel'];
const LA_KEY = 'te_last_activity';
const LOGOUT_KEY = 'te_logout_signal';

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// Mounted only for authenticated users. Enforces the shared inactivity deadline
// across tabs, re-checks elapsed time on resume, warns at T-2min and logs out at T.
const InactivityGuard = () => {
  const { token, logout } = useContext(AuthContext);
  const timeoutMs = Number(localStorage.getItem('te_timeout_ms')) || DEFAULT_TIMEOUT_MS;
  const warnMs = timeoutMs <= 4 * 60 * 1000 ? Math.floor(timeoutMs * 0.5) : timeoutMs - 2 * 60 * 1000;

  const lastActivityRef = useRef(Date.now());
  const lastRecordRef = useRef(0);
  const lastPingRef = useRef(0);
  const firedRef = useRef(false);
  const bcRef = useRef(null);
  const warningRef = useRef(false);
  const prevPolicyRef = useRef(null);
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  useEffect(() => { warningRef.current = warning; }, [warning]);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const pingServer = useCallback(() => {
    lastPingRef.current = Date.now();
    axios.post(`${API}/me/activity`, {}, auth).catch(() => {}); // eslint-disable-line
  }, [token]); // eslint-disable-line

  const setActivity = useCallback((ts, broadcast = true) => {
    lastActivityRef.current = ts;
    try { localStorage.setItem(LA_KEY, String(ts)); } catch { /* ignore */ }
    if (broadcast) { try { bcRef.current?.postMessage({ type: 'activity', ts }); } catch { /* ignore */ } }
    setWarning(false);
  }, []);

  const doLogout = useCallback(async (reason) => {
    if (firedRef.current) return;
    firedRef.current = true;
    try { await axios.post(`${API}/me/end-session`, {}, auth); } catch { /* ignore */ }
    try { bcRef.current?.postMessage({ type: 'logout' }); } catch { /* ignore */ }
    try { localStorage.setItem(LOGOUT_KEY, String(Date.now())); } catch { /* ignore */ }
    if (reason === 'timeout') {
      try { sessionStorage.setItem('te_logout_msg', 'You have been logged out after 20 minutes of inactivity. Please sign in again.'); } catch { /* ignore */ }
    }
    try { logout(); } catch { /* ignore */ }
    window.location.assign('/login');
  }, [logout, token]); // eslint-disable-line

  // Genuine interaction handler (throttled). Background work never calls this.
  const onActivity = useCallback(() => {
    // Only genuine interaction INSIDE an administrative module resets the timer.
    if (modulePolicyFor(window.location.pathname) !== 'admin') return;
    const now = Date.now();
    if (warningRef.current) return; // during the warning, only explicit "Stay signed in" resets
    if (now - lastRecordRef.current < 3000) return;
    lastRecordRef.current = now;
    setActivity(now);
    if (now - lastPingRef.current > 30000) pingServer();
  }, [setActivity, pingServer]);

  useEffect(() => {
    if (!token) return undefined;
    firedRef.current = false;
    // Seed deadline now + tell the server this device is active.
    setActivity(Date.now());
    if (modulePolicyFor(window.location.pathname) === 'admin') pingServer();
    prevPolicyRef.current = modulePolicyFor(window.location.pathname);

    try { bcRef.current = new BroadcastChannel('te_session'); } catch { bcRef.current = null; }
    if (bcRef.current) {
      bcRef.current.onmessage = (e) => {
        const m = e.data || {};
        if (m.type === 'activity' && m.ts) { lastActivityRef.current = Math.max(lastActivityRef.current, m.ts); setWarning(false); }
        if (m.type === 'logout') doLogout('silent');
      };
    }

    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

    const onStorage = (e) => {
      if (e.key === LA_KEY && e.newValue) { lastActivityRef.current = Math.max(lastActivityRef.current, Number(e.newValue)); setWarning(false); }
      if (e.key === LOGOUT_KEY) doLogout('silent');
    };
    window.addEventListener('storage', onStorage);

    const check = () => {
      // Inactivity logout applies ONLY while viewing an administrative module.
      // Operational/communication modules (CRM, ROUT, TEAM, Partners) never auto-logout.
      const policy = modulePolicyFor(window.location.pathname);
      if (policy !== 'admin') {
        prevPolicyRef.current = policy;
        if (warningRef.current) setWarning(false);
        return;
      }
      // Entering an admin module (from operational/neutral/login) starts a FRESH
      // 20-min window and refreshes the server record — so a long operational
      // session, Back/Forward, or a programmatic redirect never logs the user out.
      if (prevPolicyRef.current !== 'admin') {
        prevPolicyRef.current = 'admin';
        lastActivityRef.current = Date.now();
        try { localStorage.setItem(LA_KEY, String(lastActivityRef.current)); } catch { /* ignore */ }
        lastPingRef.current = Date.now();
        setWarning(false);
        pingServer();
        return;
      }
      // Honour cross-tab writes even without events.
      try { const ls = Number(localStorage.getItem(LA_KEY)); if (ls && ls > lastActivityRef.current) lastActivityRef.current = ls; } catch { /* ignore */ }
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= timeoutMs) { doLogout('timeout'); return; }
      if (idle >= warnMs) { setWarning(true); setSecondsLeft(Math.max(0, Math.ceil((timeoutMs - idle) / 1000))); }
      else if (warningRef.current) setWarning(false);
    };
    // Elapsed-time based (not a naive countdown): re-checks immediately on resume.
    const onResume = () => check();
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    window.addEventListener('pageshow', onResume);

    const interval = setInterval(check, 1000);
    return () => {
      clearInterval(interval);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('pageshow', onResume);
      try { bcRef.current?.close(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line
  }, [token]);

  if (!warning) return null;
  return (
    <div role="alertdialog" aria-modal="true" aria-labelledby="inactivity-title" data-testid="inactivity-warning"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <h2 id="inactivity-title" className="text-lg font-bold text-[#0E2A47]">Are you still there?</h2>
        <p className="mt-2 text-sm text-slate-600">Your session will expire in 2 minutes due to inactivity.</p>
        <p className="mt-1 text-sm font-semibold text-amber-600" data-testid="inactivity-countdown">Signing out in {fmt(secondsLeft)}</p>
        <div className="mt-5 flex justify-end gap-3">
          <button onClick={() => doLogout('manual')} data-testid="inactivity-logout-btn"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Log out</button>
          <button onClick={() => { setActivity(Date.now()); pingServer(); }} data-testid="inactivity-stay-btn"
            className="rounded-lg bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F]">Stay signed in</button>
        </div>
      </div>
    </div>
  );
};

export default InactivityGuard;
